import React from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import config from "@/config";
import { isObservabilityNoise } from "@/utils/is-observability-noise";

const isTestEnv = process.env.NODE_ENV === "test";
const appVersion = process.env.SKED_APP_BUILD_HASH || "unknown";

let bugsnagReactErrorBoundary: React.ComponentType<React.PropsWithChildren> | null =
  null;

export function initializeBugsnag(): void {
  if (isTestEnv || Bugsnag.isStarted?.()) {
    return;
  }

  Bugsnag.start({
    apiKey: config.bugsnagApiKey,
    appVersion,
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NODE_ENV,
    enabledReleaseStages: ["staging", "production"],
    collectUserIp: false,
    onError(event) {
      // Suppress expected Facebook third-party script failures from blockers and CSP.
      const message = event.errors?.[0]?.errorMessage ?? "";
      const stack =
        event.errors?.[0]?.stacktrace
          ?.map((frame) => frame.file || frame.method)
          .join("\n") ?? "";

      if (isObservabilityNoise(message, stack)) {
        return false;
      }

      if (/Failed to load script:.*connect\.facebook\.net/i.test(message)) {
        return false;
      }
      return true;
    },
  });

  window.Bugsnag = Bugsnag;
}

export function getBugsnagReactErrorBoundary(): React.ComponentType<React.PropsWithChildren> {
  if (isTestEnv) {
    return React.Fragment;
  }

  initializeBugsnag();

  if (!bugsnagReactErrorBoundary) {
    bugsnagReactErrorBoundary =
      Bugsnag.getPlugin("react")?.createErrorBoundary(React) ?? React.Fragment;
  }

  return bugsnagReactErrorBoundary;
}
